import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    AppBar, 
    Toolbar, 
    IconButton, 
    useMediaQuery, 
    Typography, 
    Drawer, 
    List, 
    ListItem, 
    ListItemText, 
    Divider, 
    Badge, 
    Menu, 
    MenuItem, 
    Tooltip, 
    Avatar,
    Slide,
    Grow
} from '@mui/material';
import { 
    FaBars, 
    FaBell, 
    FaUser, 
    FaTools, 
    FaWarehouse, 
    FaHome, 
    FaCogs, 
    FaPencilAlt, 
    FaLayerGroup, 
    FaCut, 
    FaPrint, 
    FaBoxOpen, 
    FaArrowLeft, 
    FaChevronDown, 
    FaChevronUp
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import logoImage from '../assets/logo.png';
import axiosInstance from '../api/AxiosInstance';

const DashboardLayout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isFirstBoxOpen, setFirstBoxOpen] = useState(true);
    const user = JSON.parse(localStorage.getItem('user'));

    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

    const userRef = useRef(user);

    const apiBaseURL = 'https://plasticemployers.duckdns.org';  
    const DRAWER_EXPANDED_WIDTH = 250;
    const DRAWER_COLLAPSED_WIDTH = 72;

    const shell = {
        topbarBg: 'rgba(255,255,255,0.92)',
        topbarText: '#0f172a',
        topbarBorder: 'rgba(15, 23, 42, 0.08)',
        sidebarBg: '#0b1220',
        sidebarBorder: 'rgba(148,163,184,0.12)',
        sidebarText: '#e2e8f0',
        sidebarMuted: '#94a3b8',
        accent: '#2563eb',
        mainBg: '#f8fafc',
        cardBorder: 'rgba(15, 23, 42, 0.08)',
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    
    const handleDrawerVisibilityToggle = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleLogout = () => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        navigate('/');
    };

    const handleNotificationMenuOpen = (event) => {
        setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationMenuClose = () => {
        setNotificationAnchorEl(null);
    };

    const handleNotificationClick = async (notification) => {
        const token = localStorage.getItem('access');
        try {
            await axios.post(`${apiBaseURL}/api/notifications/mark-as-read/${notification.id}/`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setNotifications(prevNotifications => 
                prevNotifications.map(n => 
                    n.id === notification.id ? { ...n, is_read: true } : n
                )
            );

            setUnreadNotificationsCount(prevCount => prevCount > 0 ? prevCount - 1 : 0);

            if (userRef.current.seccao === 'Admin' || userRef.current.seccao === 'Escritorio') {
                navigate('/escritorio');
            } else {
                navigate('/marcar-ferias');
            }
        } catch (error) {
            console.error('Erro ao marcar a notificação como lida:', error);
        }
        handleNotificationMenuClose();
    };

    useEffect(() => {
        const fetchNotifications = async () => {
            const token = localStorage.getItem('access');
            try {
                const response = await axiosInstance.get(`${apiBaseURL}/api/notifications/list/`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const notificationsData = response.data.notifications || [];
                setNotifications(notificationsData);
                const unreadCount = notificationsData.filter(n => !n.is_read).length;
                setUnreadNotificationsCount(unreadCount);
            } catch (error) {
                console.error('Erro ao buscar notificações:', error);
            }
        };

        fetchNotifications();
    }, []);  

    if (!user) {
        return <Typography>Erro: Usuário não encontrado!</Typography>;
    }

    const isAdmin = user.seccao === 'Admin';

    const grupos = isAdmin
        ? [
              { name: 'Admin', icon: <FaCogs />, color: '#FF6B6B' },
              { name: 'Escritorio', icon: <FaHome />, color: '#4ECDC4' },
              { name: 'Design', icon: <FaPencilAlt />, color: '#45B7D1' },
              { name: 'Corte', icon: <FaCut />, color: '#96CEB4' },
              { name: 'Impressao', icon: <FaPrint />, color: '#FFEAA7' },
              { name: 'Mini-Flexo', icon: <FaLayerGroup />, color: '#DDA0DD' },
              { name: 'Digital', icon: <FaTools />, color: '#98D8C8' },
              { name: 'Digital-rotativa', icon: <FaLayerGroup />, color: '#F7DC6F' },
              { name: 'Armazem', icon: <FaBoxOpen />, color: '#BB8FCE' },
          ]
        : user.seccao
        ? [{ name: user.seccao, icon: <FaTools />, color: '#4ECDC4' }]
        : [];

    if ((user.numero_empregado === '004' || user.numero_empregado === '002'|| user.numero_empregado === '003') && !isAdmin) {
        if (!grupos.find((g) => g.name === 'Corte')) grupos.push({ name: 'Corte', icon: <FaCut />, color: '#96CEB4' });
        if (!grupos.find((g) => g.name === 'Armazem')) grupos.push({ name: 'Armazem', icon: <FaBoxOpen />, color: '#BB8FCE' });
    }

    const resolveGroupPath = (groupName) => {
        if (groupName === 'Admin') return '/ferramentasadmin';
        if (groupName === 'Escritorio') return '/escritorio';
        if (groupName === 'Corte') return '/corte';
        if (groupName === 'Armazem') return '/armazem';
        if (groupName === 'Digital') return '/digital';
        return null;
    };

    const topActionButtonSx = {
        color: shell.topbarText,
        borderRadius: 1.5,
        px: { xs: 1, sm: 1.25 },
        py: 0.75,
        '&:hover': {
            backgroundColor: 'rgba(15, 23, 42, 0.06)',
        },
    };

    const menuPaperProps = {
        sx: {
            width: 280,
            borderRadius: 2,
            border: `1px solid ${shell.cardBorder}`,
            boxShadow: '0 16px 40px rgba(15,23,42,0.12)',
            mt: 1,
        },
    };

    const accountMenuPaperProps = {
        sx: {
            width: 220,
            borderRadius: 2,
            border: `1px solid ${shell.cardBorder}`,
            boxShadow: '0 16px 40px rgba(15,23,42,0.12)',
            mt: 1,
        },
    };

    const drawer = (
        <Box sx={{ 
            backgroundColor: shell.sidebarBg,
            height: '100%',
            color: shell.sidebarText
        }}>
            <Toolbar sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', py: 1, pt: 10 }}>
                <IconButton
                    onClick={handleDrawerVisibilityToggle}
                    sx={{
                        color: shell.sidebarText,
                        border: `1px solid ${shell.sidebarBorder}`,
                        borderRadius: 1.5,
                        '&:hover': { backgroundColor: 'rgba(148,163,184,0.12)' },
                    }}
                >
                    {drawerOpen ? <FaArrowLeft /> : <FaBars />}
                </IconButton>
                <Typography
                    variant="subtitle1"
                    sx={{ ml: 2, display: drawerOpen ? 'block' : 'none', fontWeight: 700, color: shell.sidebarText }}
                >
                    Menu
                </Typography>
            </Toolbar>
            <Divider sx={{ backgroundColor: shell.sidebarBorder }} />
            <List>
                {grupos.map((group, index) => {
                    const isActive = location.pathname.includes(group.name.toLowerCase());
                    return (
                        <Slide key={group.name} direction="right" in={true} timeout={Math.min(500, 120 + index * 60)}>
                            <ListItem
                                button
                                onClick={() => {
                                    const route = resolveGroupPath(group.name);
                                    if (route) navigate(route);
                                }}
                                sx={{
                                    justifyContent: drawerOpen ? 'flex-start' : 'center',
                                    px: drawerOpen ? 1.5 : 1,
                                    py: 0.75,
                                    my: 0.35,
                                    mx: 1,
                                    borderRadius: 2,
                                    border: `1px solid ${isActive ? 'rgba(148,163,184,0.22)' : 'transparent'}`,
                                    backgroundColor: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
                                    color: shell.sidebarText,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: drawerOpen ? 1.25 : 0 }}>
                                    <Box sx={{ color: isActive ? '#bfdbfe' : shell.sidebarMuted, fontSize: 16 }}>
                                        {group.icon}
                                    </Box>
                                    {drawerOpen && (
                                        <ListItemText
                                            primary={group.name}
                                            primaryTypographyProps={{
                                                fontSize: '0.92rem',
                                                fontWeight: isActive ? 700 : 500,
                                                color: shell.sidebarText,
                                            }}
                                        />
                                    )}
                                </Box>
                            </ListItem>
                        </Slide>
                    );
                })}
            </List>
        </Box>
    );
        

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    height: { xs: 56, sm: 64 },
                    backgroundColor: shell.topbarBg,
                    color: shell.topbarText,
                    backdropFilter: 'blur(10px)',
                    borderBottom: `1px solid ${shell.topbarBorder}`,
                    boxShadow: '0 4px 20px rgba(15,23,42,0.05)',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 }, px: { xs: 1, sm: 2 } }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ display: { sm: 'none' }, fontSize: { xs: 24, sm: 32 } }}
                    >
                        <FaBars />
                    </IconButton>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: 0 }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: 1.5,
                                display: 'grid',
                                placeItems: 'center',
                                backgroundColor: 'rgba(37,99,235,0.08)',
                                border: `1px solid ${shell.cardBorder}`,
                                mr: 1,
                                overflow: 'hidden',
                            }}
                        >
                            <img src={logoImage} alt="Logo" style={{ height: '24px' }} />
                        </Box>
                        <Typography
                            variant="h6"
                            noWrap
                            component="div"
                            sx={{ 
                                color: shell.topbarText,
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                display: { xs: 'none', sm: 'block' },
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            PlasticosDão
                        </Typography>
                    </Box>
                    <Box sx={{ display: { xs: 'block', sm: 'flex' }, alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            sx={{ display: { sm: 'none' }, mb: 1 }}
                            onClick={() => setFirstBoxOpen(!isFirstBoxOpen)}
                        >
                            {isFirstBoxOpen ? <FaChevronUp /> : <FaChevronDown />}
                        </IconButton>
                        {isFirstBoxOpen && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton color="inherit" onClick={() => navigate('/home')} sx={topActionButtonSx}>
                                    <FaHome style={{ fontSize: 18 }} />
                                    <Typography sx={{ ml: 1, display: { xs: 'none', sm: 'block' }, fontSize: '0.9rem' }}>Home</Typography>
                                </IconButton>
                                <IconButton color="inherit" onClick={() => navigate('/stocks')} sx={topActionButtonSx}>
                                    <FaWarehouse style={{ fontSize: 18 }} />
                                    <Typography sx={{ ml: 1, display: { xs: 'none', sm: 'block' }, fontSize: '0.9rem' }}>Stocks</Typography>
                                </IconButton>
                                <IconButton color="inherit" onClick={() => navigate('/cliches-moldes')} sx={topActionButtonSx}>
                                    <FaTools style={{ fontSize: 18 }} />
                                    <Typography sx={{ ml: 1, display: { xs: 'none', sm: 'block' }, fontSize: '0.9rem' }}>Clichês</Typography>
                                </IconButton>
                                <IconButton color="inherit" onClick={() => navigate('/op')} sx={topActionButtonSx}>
                                    <FaCogs style={{ fontSize: 18 }} />
                                    <Typography sx={{ ml: 1, display: { xs: 'none', sm: 'block' }, fontSize: '0.9rem' }}>Operacional</Typography>
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                            color="inherit"
                            onClick={handleNotificationMenuOpen}
                            sx={{ ...topActionButtonSx, color: shell.topbarText }}
                        >
                            <Badge badgeContent={unreadNotificationsCount} color="error">
                                <FaBell style={{ fontSize: 18 }} />
                            </Badge>
                        </IconButton>
                        <Menu
                            anchorEl={notificationAnchorEl}
                            open={Boolean(notificationAnchorEl)}
                            onClose={handleNotificationMenuClose}
                            PaperProps={menuPaperProps}
                        >
                            {notifications.length === 0 ? (
                                <MenuItem onClick={handleNotificationMenuClose}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhuma nova notificação
                                    </Typography>
                                </MenuItem>
                            ) : (
                                notifications.map((notification, index) => (
                                    <Tooltip key={index} title={notification.message} arrow>
                                        <MenuItem 
                                            onClick={() => handleNotificationClick(notification)} 
                                            sx={{ 
                                                whiteSpace: 'normal', 
                                                wordWrap: 'break-word', 
                                                py: 1.25,
                                                backgroundColor: notification.is_read ? 'white' : 'rgba(37, 99, 235, 0.05)',
                                                fontWeight: notification.is_read ? 400 : 600,
                                                borderLeft: notification.is_read ? 'none' : `3px solid ${shell.accent}`,
                                            }}
                                        >
                                            {notification.message}
                                        </MenuItem>
                                    </Tooltip>
                                ))
                            )}
                        </Menu>
                        <IconButton
                            color="inherit"
                            onClick={handleMenuOpen}
                            sx={{ ...topActionButtonSx, color: shell.topbarText, maxWidth: { xs: 44, sm: 'none' } }}
                        >
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(37,99,235,0.1)', color: shell.accent }}>
                                <FaUser style={{ fontSize: 16 }} />
                            </Avatar>
                            {!isMobile && (
                                <Typography variant="body2" sx={{ marginLeft: '8px', fontSize: '0.9rem', color: shell.topbarText, fontWeight: 600 }}>
                                    Bem vindo, {user.nome_completo.split(' ')[0]}
                                </Typography>
                            )}
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={accountMenuPaperProps}
                        >
                            <MenuItem onClick={() => navigate('/account-settings', { state: { user } })}>
                                Definições de conta
                            </MenuItem>
                            <MenuItem onClick={() => navigate('/marcar-ferias', { state: { user } })}>
                                Marcar Férias
                            </MenuItem>
                            <MenuItem onClick={() => navigate('/faltas')}>Faltas</MenuItem>
                            <MenuItem onClick={handleLogout}>Terminar Sessão</MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{
                    width: { sm: drawerOpen ? DRAWER_EXPANDED_WIDTH : DRAWER_COLLAPSED_WIDTH },
                    flexShrink: { sm: 0 },
                    transition: 'width 0.25s ease',
                }}
                aria-label="mailbox folders"
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_EXPANDED_WIDTH,
                            borderRight: `1px solid ${shell.sidebarBorder}`,
                        },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            width: drawerOpen ? DRAWER_EXPANDED_WIDTH : DRAWER_COLLAPSED_WIDTH,
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                            transition: 'width 0.25s ease',
                            borderRight: `1px solid ${shell.sidebarBorder}`,
                        },
                    }}
                    open={drawerOpen}
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2.5 },
                    backgroundColor: shell.mainBg,
                    minHeight: '100vh',
                }}
            >
                <Toolbar />
                <Grow in={true} timeout={800}>
                    <Box
                        sx={{
                            maxWidth: 1600,
                            mx: 'auto',
                            backgroundColor: '#ffffff',
                            border: `1px solid ${shell.cardBorder}`,
                            borderRadius: 3,
                            p: { xs: 1.5, sm: 2.5 },
                            boxShadow: '0 10px 24px rgba(15,23,42,0.04)',
                        }}
                    >
                        {children}
                    </Box>
                </Grow>
            </Box>
        </Box>
    );
};

export default DashboardLayout;
